/**
 * ⚠️ IMPORTANT: localStorage Staleness Warning
 *
 * This component persists custom model configurations (including systemPromptHash,
 * embeddingIndex, maxOpinions, etc.) to localStorage. These values are ONLY revalidated
 * against the backend when ProviderSelector is rendered and fetches fresh data.
 *
 * If the user never opens a page containing ProviderSelector, stale values (e.g. a
 * disabled/deleted system prompt hash) will be sent to the backend unchanged.
 *
 * The backend MUST independently validate any hash/reference it receives — do not rely
 * on the frontend having pruned stale values. See FileSystemPromptService.TryResolveAsync
 * which rejects inactive prompts and falls back to the default.
 *
 * When adding new settings that reference server-side entities (prompt hashes, index names,
 * provider IDs), always ensure the backend gracefully handles stale/invalid values by
 * falling back to defaults rather than failing or silently using removed resources.
 */
import React, { useEffect, useState } from 'react';
import { ProviderInfo } from '../types/provider';
import { ModelInfo, AgentType, AgentModelOverride } from '../types/models';
import { EmbeddingIndexInfo } from '../types/embedding';
import { SystemPromptSummary } from '../types/systemPrompt';
import { EvaluationProviderConfig as EvaluationProviderConfigType } from '../types/evaluation';
import { Loader2, Settings, X, Plus, Trash2, Copy, Download, GitCompare, Brain, Thermometer, Target, Dices, AlertTriangle } from 'lucide-react';
import { ApiService } from '../services/apiService';
import { EmbeddingIndexSelector } from './EmbeddingIndexSelector';
import { TextComparisonDialog, ComparisonItem } from './TextComparisonDialog';
import { useViewMode } from '../hooks/useViewMode';
import { AgentFlowDiagram } from './AgentFlowDiagram';
import { ModelTableDropdown, ModelWithProvider } from './ModelTableDropdown';
import { SystemPromptTableDropdown } from './SystemPromptTableDropdown';

interface ProviderSelectorProps {
  selectedProviders: string[];
  onSelectionChange: (providerIds: string[]) => void;
  disabled?: boolean;
  selectAllRef?: React.MutableRefObject<(() => void) | null>;
  onCustomConfigChange?: (config: Record<AgentType, AgentModelOverride | undefined>) => void;
  onCustomConfigNameChange?: (name: string) => void;
  onCustomConfigsChange?: (configs: Array<{ id: string; name: string; config: Record<AgentType, AgentModelOverride | undefined>; embeddingIndex?: string }>) => void;
  onMaxOpinionsChange?: (maxOpinions: Record<string, number>) => void;
  onMaxContraryOpinionsChange?: (maxContraryOpinions: Record<string, number>) => void;
  onMaxPassagesChange?: (maxPassages: Record<string, number>) => void;
  onEmbeddingIndexesChange?: (embeddingIndexes: Record<string, string>) => void;
  onUseReducerChange?: (useReducer: Record<string, boolean>) => void;
  onHitlChange?: (hitl: Record<string, boolean>) => void;
  onFolioThresholdChange?: (thresholds: Record<string, number>) => void;
  onRelevanceCheckChange?: (relevanceCheck: Record<string, boolean>) => void;
  onMinSearchScoreChange?: (scores: Record<string, number>) => void;
  mode?: 'standard' | 'evaluation-single' | 'evaluation-multi';
  hideAdvancedToggle?: boolean;
  hideSelectColumn?: boolean;
  hideActions?: boolean;
  showOnlyCustomConfigs?: boolean;
  savedEvaluationConfigs?: EvaluationProviderConfigType[];
}

interface CustomConfig {
  id: string;
  name: string;
  config: Record<AgentType, AgentModelOverride | undefined>;
  maxOpinions?: number;
  maxContraryOpinions?: number;
  maxPassagesPerDocument?: number;
  embeddingIndex?: string;
  enableHitl?: boolean;
  folioThreshold?: number;
  enableRelevanceCheck?: boolean;
  minimumSearchScore?: number;
}

const AGENT_LABELS: Record<AgentType, string> = {
  orchestrator: 'Orchestrator',
  researcher: 'Researcher',
  reducer: 'Reducer',
  writer: 'Writer',
  summarizer: 'Summarizer',
  relevancechecker: 'Relevance Checker'
};

const AGENT_DESCRIPTIONS: Record<string, string> = {
  orchestrator: 'First agent in the pipeline. Decides whether to block the query (non-legal), ask clarifying questions, or proceed with research.',
  researcher: 'Searches the legal database using multiple query styles to find relevant court opinions and passages.',
  reducer: 'Trims down large passages to only the most relevant excerpts before sending to the Writer. Saves tokens and improves focus.',
  writer: 'Composes the final answer based on research results. Includes citations, confidence score, and acknowledges contrary authority.',
  relevancechecker: 'Filters out search results that are not actually relevant to the query, preventing noise from reaching the Writer.'
};

const CUSTOM_CONFIGS_KEY = 'custom-model-configs';
const SELECTED_CONFIGS_KEY = 'selected-custom-config-ids';
const PROVIDER_MAX_OPINIONS_KEY = 'provider-max-opinions';
const PROVIDER_MAX_CONTRARY_OPINIONS_KEY = 'provider-max-contrary-opinions';
const PROVIDER_MAX_PASSAGES_KEY = 'provider-max-passages';
const PROVIDER_EMBEDDING_INDEXES_KEY = 'provider-embedding-indexes';
const PROVIDER_REDUCER_KEY = 'provider-use-reducer';
const PROVIDER_HITL_KEY = 'provider-hitl';
const PROVIDER_FOLIO_KEY = 'provider-folio-threshold';
const PROVIDER_RELEVANCE_CHECK_KEY = 'provider-relevance-check';
const PROVIDER_MIN_SEARCH_SCORE_KEY = 'provider-min-search-score';

export const ProviderSelector: React.FC<ProviderSelectorProps> = ({
  selectedProviders,
  onSelectionChange,
  disabled = false,
  selectAllRef,
  onCustomConfigChange,
  onCustomConfigNameChange,
  onCustomConfigsChange,
  onMaxOpinionsChange,
  onMaxContraryOpinionsChange,
  onMaxPassagesChange,
  onEmbeddingIndexesChange,
  onUseReducerChange,
  onHitlChange,
  onFolioThresholdChange,
  onRelevanceCheckChange,
  onMinSearchScoreChange,
  mode = 'standard',
  hideAdvancedToggle = false,
  hideSelectColumn = false,
  hideActions = false,
  showOnlyCustomConfigs = false,
  savedEvaluationConfigs
}) => {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<Record<string, ModelInfo[]>>({});
  const [editingAgent, setEditingAgent] = useState<AgentType | null>(null);
  const [tempConfig, setTempConfig] = useState<AgentModelOverride>({ 
    providerType: '', 
    modelId: '', 
    temperature: undefined,
    systemPromptHash: undefined,
    reasoningEffort: undefined
  });
  const [customConfigs, setCustomConfigs] = useState<CustomConfig[]>([]);
  const [selectedCustomConfigIds, setSelectedCustomConfigIds] = useState<string[]>([]);
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [newConfigName, setNewConfigName] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [editingNameConfigId, setEditingNameConfigId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [providerMaxOpinions, setProviderMaxOpinions] = useState<Record<string, number>>({});
  const [providerMaxContraryOpinions, setProviderMaxContraryOpinions] = useState<Record<string, number>>({});
  const [providerMaxPassages, setProviderMaxPassages] = useState<Record<string, number>>({});

  const [embeddingIndexes, setEmbeddingIndexes] = useState<EmbeddingIndexInfo[]>([]);
  const [providerEmbeddingIndexes, setProviderEmbeddingIndexes] = useState<Record<string, string>>({});
  const [providerHitl, setProviderHitl] = useState<Record<string, boolean>>({});
  const [providerReducer, setProviderReducer] = useState<Record<string, boolean>>({});
  const [providerFolioThreshold, setProviderFolioThreshold] = useState<Record<string, number>>({});
  const [providerRelevanceCheck, setProviderRelevanceCheck] = useState<Record<string, boolean>>({});
  const [providerMinSearchScore, setProviderMinSearchScore] = useState<Record<string, number>>({});
  const [showEmbeddingIndexDialog, setShowEmbeddingIndexDialog] = useState(false);
  const [embeddingIndexDialogTarget, setEmbeddingIndexDialogTarget] = useState<{ id: string; isCustomConfig: boolean } | null>(null);
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptSummary[]>([]);
  const [showPromptComparison, setShowPromptComparison] = useState(false);
  const [promptComparisonItems, setPromptComparisonItems] = useState<ComparisonItem[]>([]);
  const apiService = ApiService.getInstance();
  const { viewMode } = useViewMode();
  const showCost = viewMode === 'admin';
  const showWarnings = viewMode === 'admin';
  const [hoveredAgent, setHoveredAgent] = useState<AgentType | null>(null);

  // Warning threshold: total opinions (supporting + contrary) above this without Reducer triggers warning
  const OPINIONS_THRESHOLD = 12;
  const MAX_PASSAGES_THRESHOLD = 10;

  /** Compute warnings for a given row configuration */
  const getRowWarnings = (opts: {
    useReducer: boolean;
    enableRelevanceCheck: boolean;
    maxOpinions: number;
    maxContraryOpinions: number;
    maxPassages: number;
    agentConfigs: Record<AgentType, AgentModelOverride | undefined>;
  }) => {
    if (!showWarnings) return { opinions: false, relevance: false, passages: false, prompts: [] as AgentType[] };

    const totalOpinions = opts.maxOpinions + opts.maxContraryOpinions;
    const opinions = !opts.useReducer && totalOpinions > OPINIONS_THRESHOLD;
    const relevance = opts.enableRelevanceCheck && totalOpinions > OPINIONS_THRESHOLD;
    const passages = opts.maxPassages > MAX_PASSAGES_THRESHOLD;

    // Check for non-default system prompts (not the newest per agent)
    const prompts: AgentType[] = [];
    for (const agent of Object.keys(opts.agentConfigs) as AgentType[]) {
      const cfg = opts.agentConfigs[agent];
      if (!cfg?.systemPromptHash) continue;
      const agentPrompts = systemPrompts.filter(p => p.agentType === agent);
      if (agentPrompts.length === 0) continue;
      const newestHash = agentPrompts[0].hash; // API returns newest first
      if (cfg.systemPromptHash !== newestHash) {
        prompts.push(agent);
      }
    }

    return { opinions, relevance, passages, prompts };
  };

  const WarningIcon: React.FC<{ message: string }> = ({ message }) => (
    <span className="inline-flex items-center ml-1" title={message}>
      <AlertTriangle className="w-4 h-4 text-amber-500" />
    </span>
  );

  useEffect(() => {
    fetchData();
    loadCustomConfigs();
    loadSelectedConfig();
    loadProviderMaxOpinions();
    loadProviderMaxContraryOpinions();
    loadProviderMaxPassages();
    loadProviderEmbeddingIndexes();
    loadProviderReducer();
    loadProviderHitl();
    loadProviderFolio();
    loadProviderRelevanceCheck();
    loadProviderMinSearchScore();
  }, []);

  const loadCustomConfigs = () => {
    try {
      const stored = localStorage.getItem(CUSTOM_CONFIGS_KEY);
      if (stored) {
        setCustomConfigs(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load custom configs:', error);
    }
  };

  const loadSelectedConfig = () => {
    try {
      const stored = localStorage.getItem(SELECTED_CONFIGS_KEY);
      if (stored) {
        const ids = JSON.parse(stored);
        setSelectedCustomConfigIds(ids);
        
        // Load configs into parent
        const configs = JSON.parse(localStorage.getItem(CUSTOM_CONFIGS_KEY) || '[]');
        const selectedConfigs = configs.filter((c: CustomConfig) => ids.includes(c.id));
        
        if (onCustomConfigsChange && selectedConfigs.length > 0) {
          onCustomConfigsChange(selectedConfigs);
        }
        
        // Backward compatibility: also set single config if only one selected
        if (selectedConfigs.length === 1 && onCustomConfigChange) {
          onCustomConfigChange(selectedConfigs[0].config);
          if (onCustomConfigNameChange) {
            onCustomConfigNameChange(selectedConfigs[0].name);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load selected config:', error);
    }
  };

  const loadProviderMaxOpinions = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_MAX_OPINIONS_KEY);
      if (stored) {
        const maxOpinions = JSON.parse(stored);
        setProviderMaxOpinions(maxOpinions);
        
        // Notify parent component
        if (onMaxOpinionsChange) {
          onMaxOpinionsChange(maxOpinions);
        }
      }
    } catch (error) {
      console.error('Failed to load provider max opinions:', error);
    }
  };

  const loadProviderMaxPassages = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_MAX_PASSAGES_KEY);
      if (stored) {
        const maxPassages = JSON.parse(stored);
        setProviderMaxPassages(maxPassages);
        
        // Notify parent component
        if (onMaxPassagesChange) {
          onMaxPassagesChange(maxPassages);
        }
      }
    } catch (error) {
      console.error('Failed to load provider max passages:', error);
    }
  };

  const loadProviderMaxContraryOpinions = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_MAX_CONTRARY_OPINIONS_KEY);
      if (stored) {
        const maxContraryOpinions = JSON.parse(stored);
        setProviderMaxContraryOpinions(maxContraryOpinions);
        
        if (onMaxContraryOpinionsChange) {
          onMaxContraryOpinionsChange(maxContraryOpinions);
        }
      }
    } catch (error) {
      console.error('Failed to load provider max contrary opinions:', error);
    }
  };

  const handleOpenEmbeddingIndexDialog = (id: string, isCustomConfig: boolean) => {
    setEmbeddingIndexDialogTarget({ id, isCustomConfig });
    setShowEmbeddingIndexDialog(true);
  };

  const handleSelectEmbeddingIndex = (indexName: string) => {
    if (!embeddingIndexDialogTarget) return;

    const { id, isCustomConfig } = embeddingIndexDialogTarget;

    if (isCustomConfig) {
      // Update custom config
      const configIndex = customConfigs.findIndex(c => c.id === id);
      if (configIndex !== -1) {
        const updatedConfigs = [...customConfigs];
        updatedConfigs[configIndex] = {
          ...updatedConfigs[configIndex],
          embeddingIndex: indexName || undefined
        };
        saveCustomConfigs(updatedConfigs);

        if (selectedCustomConfigIds.includes(id)) {
          const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
          if (onCustomConfigsChange) {
            onCustomConfigsChange(selectedConfigs);
          }
        }
      }
    } else {
      // Update regular provider
      const updated = { ...providerEmbeddingIndexes, [id]: indexName };
      saveProviderEmbeddingIndexes(updated);
    }

    setShowEmbeddingIndexDialog(false);
    setEmbeddingIndexDialogTarget(null);
  };

  const handleCancelEmbeddingIndexDialog = () => {
    setShowEmbeddingIndexDialog(false);
    setEmbeddingIndexDialogTarget(null);
  };

  const loadProviderEmbeddingIndexes = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_EMBEDDING_INDEXES_KEY);
      if (stored) {
        const embeddingIndexes = JSON.parse(stored);
        setProviderEmbeddingIndexes(embeddingIndexes);
        
        // Notify parent component
        if (onEmbeddingIndexesChange) {
          onEmbeddingIndexesChange(embeddingIndexes);
        }
      }
    } catch (error) {
      console.error('Failed to load provider embedding indexes:', error);
    }
  };

  const loadProviderHitl = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_HITL_KEY);
      if (stored) {
        const hitl = JSON.parse(stored);
        setProviderHitl(hitl);
        
        if (onHitlChange) {
          onHitlChange(hitl);
        }
      }
    } catch (error) {
      console.error('Failed to load provider HITL settings:', error);
    }
  };

  const loadProviderReducer = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_REDUCER_KEY);
      if (stored) {
        const reducer = JSON.parse(stored);
        setProviderReducer(reducer);
        
        if (onUseReducerChange) {
          onUseReducerChange(reducer);
        }
      }
    } catch (error) {
      console.error('Failed to load provider reducer settings:', error);
    }
  };

  const saveProviderReducer = (reducer: Record<string, boolean>) => {
    try {
      localStorage.setItem(PROVIDER_REDUCER_KEY, JSON.stringify(reducer));
      setProviderReducer(reducer);
      
      if (onUseReducerChange) {
        onUseReducerChange(reducer);
      }
    } catch (error) {
      console.error('Failed to save provider reducer settings:', error);
    }
  };

  const saveProviderHitl = (hitl: Record<string, boolean>) => {
    try {
      localStorage.setItem(PROVIDER_HITL_KEY, JSON.stringify(hitl));
      setProviderHitl(hitl);
      
      if (onHitlChange) {
        onHitlChange(hitl);
      }
    } catch (error) {
      console.error('Failed to save provider HITL settings:', error);
    }
  };

  const loadProviderFolio = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_FOLIO_KEY);
      if (stored) {
        const thresholds = JSON.parse(stored);
        setProviderFolioThreshold(thresholds);
        
        if (onFolioThresholdChange) {
          onFolioThresholdChange(thresholds);
        }
      }
    } catch (error) {
      console.error('Failed to load provider FOLIO settings:', error);
    }
  };

  const saveProviderFolio = (thresholds: Record<string, number>) => {
    try {
      localStorage.setItem(PROVIDER_FOLIO_KEY, JSON.stringify(thresholds));
      setProviderFolioThreshold(thresholds);
      
      if (onFolioThresholdChange) {
        onFolioThresholdChange(thresholds);
      }
    } catch (error) {
      console.error('Failed to save provider FOLIO settings:', error);
    }
  };

  const loadProviderRelevanceCheck = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_RELEVANCE_CHECK_KEY);
      if (stored) {
        const relevanceCheck = JSON.parse(stored);
        setProviderRelevanceCheck(relevanceCheck);
        
        if (onRelevanceCheckChange) {
          onRelevanceCheckChange(relevanceCheck);
        }
      }
    } catch (error) {
      console.error('Failed to load provider relevance check settings:', error);
    }
  };

  const saveProviderRelevanceCheck = (relevanceCheck: Record<string, boolean>) => {
    try {
      localStorage.setItem(PROVIDER_RELEVANCE_CHECK_KEY, JSON.stringify(relevanceCheck));
      setProviderRelevanceCheck(relevanceCheck);
      
      if (onRelevanceCheckChange) {
        onRelevanceCheckChange(relevanceCheck);
      }
    } catch (error) {
      console.error('Failed to save provider relevance check settings:', error);
    }
  };

  const loadProviderMinSearchScore = () => {
    try {
      const stored = localStorage.getItem(PROVIDER_MIN_SEARCH_SCORE_KEY);
      if (stored) {
        const scores = JSON.parse(stored);
        setProviderMinSearchScore(scores);
        
        if (onMinSearchScoreChange) {
          onMinSearchScoreChange(scores);
        }
      }
    } catch (error) {
      console.error('Failed to load provider min search score settings:', error);
    }
  };

  const saveProviderMinSearchScore = (scores: Record<string, number>) => {
    try {
      localStorage.setItem(PROVIDER_MIN_SEARCH_SCORE_KEY, JSON.stringify(scores));
      setProviderMinSearchScore(scores);
      
      if (onMinSearchScoreChange) {
        onMinSearchScoreChange(scores);
      }
    } catch (error) {
      console.error('Failed to save provider min search score settings:', error);
    }
  };

  const saveProviderMaxOpinions = (maxOpinions: Record<string, number>) => {
    try {
      localStorage.setItem(PROVIDER_MAX_OPINIONS_KEY, JSON.stringify(maxOpinions));
      setProviderMaxOpinions(maxOpinions);
      
      // Notify parent component
      if (onMaxOpinionsChange) {
        onMaxOpinionsChange(maxOpinions);
      }
    } catch (error) {
      console.error('Failed to save provider max opinions:', error);
    }
  };

  const saveProviderMaxPassages = (maxPassages: Record<string, number>) => {
    try {
      localStorage.setItem(PROVIDER_MAX_PASSAGES_KEY, JSON.stringify(maxPassages));
      setProviderMaxPassages(maxPassages);
      
      // Notify parent component
      if (onMaxPassagesChange) {
        onMaxPassagesChange(maxPassages);
      }
    } catch (error) {
      console.error('Failed to save provider max passages:', error);
    }
  };

  const saveProviderMaxContraryOpinions = (maxContraryOpinions: Record<string, number>) => {
    try {
      localStorage.setItem(PROVIDER_MAX_CONTRARY_OPINIONS_KEY, JSON.stringify(maxContraryOpinions));
      setProviderMaxContraryOpinions(maxContraryOpinions);
      
      if (onMaxContraryOpinionsChange) {
        onMaxContraryOpinionsChange(maxContraryOpinions);
      }
    } catch (error) {
      console.error('Failed to save provider max contrary opinions:', error);
    }
  };

  const saveProviderEmbeddingIndexes = (embeddingIndexes: Record<string, string>) => {
    try {
      localStorage.setItem(PROVIDER_EMBEDDING_INDEXES_KEY, JSON.stringify(embeddingIndexes));
      setProviderEmbeddingIndexes(embeddingIndexes);
      
      // Notify parent component
      if (onEmbeddingIndexesChange) {
        onEmbeddingIndexesChange(embeddingIndexes);
      }
    } catch (error) {
      console.error('Failed to save provider embedding indexes:', error);
    }
  };

  const saveCustomConfigs = (configs: CustomConfig[]) => {
    try {
      localStorage.setItem(CUSTOM_CONFIGS_KEY, JSON.stringify(configs));
      setCustomConfigs(configs);
    } catch (error) {
      console.error('Failed to save custom configs:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [providersData, modelsData, embeddingIndexesData, systemPromptsData] = await Promise.all([
        apiService.getProviders(),
        apiService.getAvailableModels(),
        apiService.getEmbeddingIndexes(),
        apiService.getSystemPrompts(false) // Only fetch active prompts
      ]);
      setProviders(providersData);
      setAvailableModels(modelsData.modelsByProvider || {});
      setEmbeddingIndexes(embeddingIndexesData);
      setSystemPrompts(systemPromptsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderToggle = (providerId: string) => {
    if (disabled) return;
    
    if (mode === 'evaluation-single') {
      // Single select mode - replace selection
      onSelectionChange([providerId]);
    } else {
      // Multi select mode
      const newSelection = selectedProviders.includes(providerId)
        ? selectedProviders.filter(id => id !== providerId)
        : [...selectedProviders, providerId];
      
      onSelectionChange(newSelection);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    
    const enabledProviderIds = providers
      .filter(p => p.isEnabled)
      .map(p => p.id);
    
    const allSelected = enabledProviderIds.every(id => selectedProviders.includes(id));
    
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(enabledProviderIds);
    }
  };

  const handleCustomConfigSelect = (configId: string) => {
    let newSelection: string[];
    
    if (mode === 'evaluation-single') {
      // Single select mode - replace selection
      newSelection = [configId];
    } else {
      // Multi select mode
      newSelection = selectedCustomConfigIds.includes(configId)
        ? selectedCustomConfigIds.filter(id => id !== configId)
        : [...selectedCustomConfigIds, configId];
    }
    
    setSelectedCustomConfigIds(newSelection);
    
    // Only save to localStorage in standard mode
    if (mode === 'standard') {
      localStorage.setItem(SELECTED_CONFIGS_KEY, JSON.stringify(newSelection));
    }
    
    // Notify parent via onSelectionChange (for evaluation modes)
    if (mode === 'evaluation-single' || mode === 'evaluation-multi') {
      onSelectionChange(newSelection);
    }
    
    // Notify parent with all selected configs
    const selectedConfigs = customConfigs.filter(c => newSelection.includes(c.id));
    if (onCustomConfigsChange) {
      onCustomConfigsChange(selectedConfigs);
    }
    
    // Backward compatibility: also set single config if only one selected
    if (selectedConfigs.length === 1) {
      if (onCustomConfigChange) {
        onCustomConfigChange(selectedConfigs[0].config);
      }
      if (onCustomConfigNameChange) {
        onCustomConfigNameChange(selectedConfigs[0].name);
      }
    } else if (selectedConfigs.length === 0) {
      if (onCustomConfigChange) {
        const emptyConfig: Record<AgentType, AgentModelOverride | undefined> = {
          orchestrator: undefined,
          researcher: undefined,
          reducer: undefined,
          writer: undefined,
          summarizer: undefined,
          relevancechecker: undefined
        };
        onCustomConfigChange(emptyConfig);
      }
      if (onCustomConfigNameChange) {
        onCustomConfigNameChange('');
      }
    }
  };

  const handleStartNewConfig = () => {
    setIsCreatingNew(true);
    setNewConfigName('');
    setEditingConfigId(crypto.randomUUID());
  };

  const handleSaveNewConfig = () => {
    if (!newConfigName.trim()) {
      alert('Please enter a configuration name');
      return;
    }

    const newConfig: CustomConfig = {
      id: editingConfigId!,
      name: newConfigName.trim(),
      config: {
        orchestrator: undefined,
        researcher: undefined,
        reducer: undefined,
        writer: undefined,
        summarizer: undefined,
        relevancechecker: undefined
      }
    };

    const updatedConfigs = [...customConfigs, newConfig];
    saveCustomConfigs(updatedConfigs);
    
    // In evaluation modes, automatically select the newly created config
    if (mode === 'evaluation-single' || mode === 'evaluation-multi') {
      onSelectionChange([newConfig.id]);
    }
    
    setIsCreatingNew(false);
    setEditingConfigId(null);
    setNewConfigName('');
  };

  const handleDeleteConfig = (configId: string) => {
    if (confirm('Delete this configuration?')) {
      saveCustomConfigs(customConfigs.filter(c => c.id !== configId));
      
      // Remove from selection if selected
      if (selectedCustomConfigIds.includes(configId)) {
        const newSelection = selectedCustomConfigIds.filter(id => id !== configId);
        setSelectedCustomConfigIds(newSelection);
        localStorage.setItem(SELECTED_CONFIGS_KEY, JSON.stringify(newSelection));
        
        const selectedConfigs = customConfigs.filter(c => newSelection.includes(c.id));
        if (onCustomConfigsChange) {
          onCustomConfigsChange(selectedConfigs);
        }
        
        if (selectedConfigs.length === 0) {
          if (onCustomConfigChange) {
            const emptyConfig: Record<AgentType, AgentModelOverride | undefined> = {
              orchestrator: undefined,
              researcher: undefined,
              reducer: undefined,
              writer: undefined,
              summarizer: undefined,
              relevancechecker: undefined
            };
            onCustomConfigChange(emptyConfig);
          }
          if (onCustomConfigNameChange) {
            onCustomConfigNameChange('');
          }
        }
      }
    }
  };

  const handleCellClick = (configId: string, agentType: AgentType) => {
    if (disabled) return;
    
    const config = customConfigs.find(c => c.id === configId);
    if (!config) return;

    const existing = config.config[agentType];
    
    // Filter system prompts by agent type and get the newest one as default
    const agentSystemPrompts = systemPrompts.filter(p => p.agentType === agentType);
    const newestPromptHash = agentSystemPrompts.length > 0 ? agentSystemPrompts[0].hash : undefined;
    
    setTempConfig(existing || { 
      providerType: '', 
      modelId: '', 
      temperature: undefined,
      topP: undefined,
      seed: undefined,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
      systemPromptHash: newestPromptHash,
      reasoningEffort: undefined
    });
    setEditingAgent(agentType);
    setEditingConfigId(configId);
  };

  const handleSystemPromptChange = (systemPromptHash: string) => {
    setTempConfig({
      ...tempConfig,
      systemPromptHash: systemPromptHash || undefined
    });
  };

  const handleOpenPromptComparison = () => {
    const agentPrompts = systemPrompts.filter(p => editingAgent && p.agentType === editingAgent);
    const items: ComparisonItem[] = agentPrompts.map(p => ({
      id: p.hash,
      label: p.description,
      description: p.purpose,
      content: '', // Loaded lazily via fetchContent
      createdAt: p.createdAt
    }));
    setPromptComparisonItems(items);
    setShowPromptComparison(true);
  };

  const fetchPromptContent = async (hash: string): Promise<string> => {
    const prompt = await apiService.getSystemPromptByHash(hash);
    return prompt?.content || '[Content not found]';
  };

  const handlePromptComparisonApprove = (hash: string) => {
    handleSystemPromptChange(hash);
    setShowPromptComparison(false);
  };

  const handleModelChange = (modelId: string, providerType: string) => {
    const models = availableModels[providerType] || [];
    const selectedModel = models.find(m => m.modelId === modelId);
    setTempConfig({
      ...tempConfig,
      providerType,
      modelId,
      temperature: selectedModel?.isTemperatureAllowed ? selectedModel.defaultTemperature : undefined,
      topP: selectedModel?.defaultTopP,
      seed: selectedModel?.defaultSeed,
      frequencyPenalty: selectedModel?.defaultFrequencyPenalty,
      presencePenalty: selectedModel?.defaultPresencePenalty,
      reasoningEffort: selectedModel?.isThinkingModel ? selectedModel.defaultReasoningEffort : undefined
    });
  };

  const handleTemperatureChange = (temperature: number) => {
    setTempConfig({
      ...tempConfig,
      temperature
    });
  };

  const handleTopPChange = (topP: number) => {
    setTempConfig({
      ...tempConfig,
      topP
    });
  };

  const handleSeedChange = (seed: number) => {
    setTempConfig({
      ...tempConfig,
      seed
    });
  };

  const handleFrequencyPenaltyChange = (frequencyPenalty: number) => {
    setTempConfig({
      ...tempConfig,
      frequencyPenalty
    });
  };

  const handleReasoningEffortChange = (reasoningEffort: string) => {
    setTempConfig({
      ...tempConfig,
      reasoningEffort: reasoningEffort || undefined
    });
  };

  const handlePresencePenaltyChange = (presencePenalty: number) => {
    setTempConfig({
      ...tempConfig,
      presencePenalty
    });
  };

  const handleSaveAgentConfig = () => {
    if (!editingAgent || !editingConfigId) return;
    
    if (!tempConfig.providerType || !tempConfig.modelId) {
      alert('Please select both provider and model');
      return;
    }

    const configIndex = customConfigs.findIndex(c => c.id === editingConfigId);
    if (configIndex === -1) return;

    const updatedConfigs = [...customConfigs];
    updatedConfigs[configIndex] = {
      ...updatedConfigs[configIndex],
      config: {
        ...updatedConfigs[configIndex].config,
        [editingAgent]: tempConfig
      }
    };

    saveCustomConfigs(updatedConfigs);

    // If this config is currently selected, update the active configs
    if (selectedCustomConfigIds.includes(editingConfigId)) {
      const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
      if (onCustomConfigsChange) {
        onCustomConfigsChange(selectedConfigs);
      }
      
      // Backward compatibility
      if (selectedConfigs.length === 1 && onCustomConfigChange) {
        onCustomConfigChange(selectedConfigs[0].config);
      }
    }

    setEditingAgent(null);
    setEditingConfigId(null);
  };

  const handleCancelEdit = () => {
    setEditingAgent(null);
    setEditingConfigId(null);
    setTempConfig({ 
      providerType: '', 
      modelId: '', 
      temperature: undefined,
      topP: undefined,
      seed: undefined,
      frequencyPenalty: undefined,
      presencePenalty: undefined,
      systemPromptHash: undefined,
      reasoningEffort: undefined
    });
  };

  const handleCancelNewConfig = () => {
    setIsCreatingNew(false);
    setEditingConfigId(null);
    setNewConfigName('');
  };

  const handleCopyConfig = async (configId: string) => {
    const config = customConfigs.find(c => c.id === configId);
    if (!config) return;

    const exportData = {
      version: 1,
      name: config.name,
      config: config.config,
      maxOpinions: config.maxOpinions || 5,
      maxContraryOpinions: config.maxContraryOpinions,
      maxPassagesPerDocument: config.maxPassagesPerDocument || 3,
      embeddingIndex: config.embeddingIndex,
      enableHitl: config.enableHitl,
      folioThreshold: config.folioThreshold ?? 6,
      enableRelevanceCheck: config.enableRelevanceCheck,
      minimumSearchScore: config.minimumSearchScore ?? 0.7
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
      setCopyFeedback(configId);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Failed to copy configuration:', error);
      alert('Failed to copy configuration to clipboard');
    }
  };

  const handleCopyProviderAsConfig = async (provider: ProviderInfo) => {
    const agentTypes: AgentType[] = ['orchestrator', 'researcher', 'reducer', 'writer', 'summarizer', 'relevancechecker'];
    const config: Record<AgentType, AgentModelOverride | undefined> = {
      orchestrator: undefined,
      researcher: undefined,
      reducer: undefined,
      writer: undefined,
      summarizer: undefined,
      relevancechecker: undefined
    };

    for (const agent of agentTypes) {
      const modelId = provider.agentModels[agent];
      if (modelId) {
        const model = (availableModels[provider.providerType] || []).find(m => m.modelId === modelId);
        config[agent] = {
          providerType: provider.providerType,
          modelId,
          temperature: model?.isTemperatureAllowed ? model.defaultTemperature : undefined,
          topP: model?.defaultTopP,
          seed: model?.defaultSeed,
          frequencyPenalty: model?.defaultFrequencyPenalty,
          presencePenalty: model?.defaultPresencePenalty,
          reasoningEffort: model?.isThinkingModel ? model.defaultReasoningEffort : undefined,
          useReducer: agent === 'reducer' ? (providerReducer[provider.id] !== false) : undefined
        };
      }
    }

    const exportData = {
      version: 1,
      name: provider.displayName,
      config,
      maxOpinions: providerMaxOpinions[provider.id] || 5,
      maxContraryOpinions: providerMaxContraryOpinions[provider.id],
      maxPassagesPerDocument: providerMaxPassages[provider.id] || 3,
      embeddingIndex: providerEmbeddingIndexes[provider.id] || undefined,
      enableHitl: providerHitl[provider.id] !== false,
      folioThreshold: providerFolioThreshold[provider.id] ?? 6,
      enableRelevanceCheck: providerRelevanceCheck[provider.id] || false,
      minimumSearchScore: providerMinSearchScore[provider.id] ?? 0.7
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
      setCopyFeedback(provider.id);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Failed to copy provider configuration:', error);
      alert('Failed to copy configuration to clipboard');
    }
  };

  const handleImportConfig = () => {
    setShowImportDialog(true);
    setImportText('');
  };

  const handlePasteConfig = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setImportText(text);
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      alert('Failed to read from clipboard. Please paste manually.');
    }
  };

  const handleValidateAndImport = () => {
    try {
      const data = JSON.parse(importText);
      
      // Validate structure
      if (!data.version || !data.name || !data.config) {
        alert('Invalid configuration format: missing required fields (version, name, config)');
        return;
      }

      if (data.version !== 1) {
        alert(`Unsupported configuration version: ${data.version}`);
        return;
      }

      // Validate config structure - allow partial configs (missing agents default to undefined)
      const allAgents: AgentType[] = ['orchestrator', 'researcher', 'reducer', 'writer', 'summarizer', 'relevancechecker'];
      for (const agent of allAgents) {
        const agentConfig = data.config[agent];
        if (agentConfig !== undefined && agentConfig !== null) {
          if (!agentConfig.providerType || !agentConfig.modelId) {
            alert(`Invalid configuration for agent "${agent}": missing providerType or modelId`);
            return;
          }
        }
      }

      // Normalize: ensure all agent keys exist (missing ones become undefined)
      for (const agent of allAgents) {
        if (!(agent in data.config)) {
          data.config[agent] = undefined;
        }
      }

      // Check if name already exists
      const nameExists = customConfigs.some(c => c.name === data.name);
      let finalName = data.name;
      if (nameExists) {
        const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
        finalName = `${data.name} (imported ${timestamp})`;
      }

      // Create new config
      const newConfig: CustomConfig = {
        id: crypto.randomUUID(),
        name: finalName,
        config: data.config,
        maxOpinions: data.maxOpinions || 5,
        maxContraryOpinions: data.maxContraryOpinions,
        maxPassagesPerDocument: data.maxPassagesPerDocument || 3,
        embeddingIndex: data.embeddingIndex,
        enableHitl: data.enableHitl,
        folioThreshold: data.folioThreshold ?? 6,
        enableRelevanceCheck: data.enableRelevanceCheck,
        minimumSearchScore: data.minimumSearchScore
      };

      saveCustomConfigs([...customConfigs, newConfig]);
      setShowImportDialog(false);
      setImportText('');
      alert(`Configuration "${finalName}" imported successfully!`);
    } catch (error) {
      console.error('Failed to import configuration:', error);
      alert('Failed to import configuration. Please check the format and try again.');
    }
  };

  const handleCancelImport = () => {
    setShowImportDialog(false);
    setImportText('');
  };

  const handleStartEditName = (configId: string, currentName: string) => {
    setEditingNameConfigId(configId);
    setEditingNameValue(currentName);
  };

  const handleSaveEditName = () => {
    if (!editingNameConfigId || !editingNameValue.trim()) {
      alert('Please enter a configuration name');
      return;
    }

    const configIndex = customConfigs.findIndex(c => c.id === editingNameConfigId);
    if (configIndex === -1) return;

    const updatedConfigs = [...customConfigs];
    updatedConfigs[configIndex] = {
      ...updatedConfigs[configIndex],
      name: editingNameValue.trim()
    };

    saveCustomConfigs(updatedConfigs);

    // If this config is currently selected, update the active name
    if (selectedCustomConfigIds.includes(editingNameConfigId)) {
      const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
      if (onCustomConfigsChange) {
        onCustomConfigsChange(selectedConfigs);
      }
      
      // Backward compatibility
      if (selectedConfigs.length === 1 && onCustomConfigNameChange) {
        onCustomConfigNameChange(selectedConfigs[0].name);
      }
    }

    setEditingNameConfigId(null);
    setEditingNameValue('');
  };

  const handleCancelEditName = () => {
    setEditingNameConfigId(null);
    setEditingNameValue('');
  };



  const handleReducerToggle = (configId: string, currentValue: boolean) => {
    if (disabled) return;

    // Update the custom config with the new reducer setting
    const configIndex = customConfigs.findIndex(c => c.id === configId);
    if (configIndex !== -1) {
      const updatedConfigs = [...customConfigs];
      const existingReducerConfig = updatedConfigs[configIndex].config.reducer;
      
      updatedConfigs[configIndex] = {
        ...updatedConfigs[configIndex],
        config: {
          ...updatedConfigs[configIndex].config,
          reducer: {
            providerType: existingReducerConfig?.providerType || '',
            modelId: existingReducerConfig?.modelId || '',
            temperature: existingReducerConfig?.temperature,
            topP: existingReducerConfig?.topP,
            seed: existingReducerConfig?.seed,
            frequencyPenalty: existingReducerConfig?.frequencyPenalty,
            presencePenalty: existingReducerConfig?.presencePenalty,
            useReducer: !currentValue
          }
        }
      };
      saveCustomConfigs(updatedConfigs);

      // If this config is currently selected, update the active configs
      if (selectedCustomConfigIds.includes(configId)) {
        const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
        if (onCustomConfigsChange) {
          onCustomConfigsChange(selectedConfigs);
        }
        
        // Backward compatibility
        if (selectedConfigs.length === 1 && onCustomConfigChange) {
          onCustomConfigChange(selectedConfigs[0].config);
        }
      }
    }
  };

  const handleHitlToggle = (id: string, isCustomConfig: boolean) => {
    if (disabled) return;

    if (isCustomConfig) {
      const configIndex = customConfigs.findIndex(c => c.id === id);
      if (configIndex !== -1) {
        const updatedConfigs = [...customConfigs];
        updatedConfigs[configIndex] = {
          ...updatedConfigs[configIndex],
          enableHitl: updatedConfigs[configIndex].enableHitl === false ? true : false
        };
        saveCustomConfigs(updatedConfigs);

        if (selectedCustomConfigIds.includes(id)) {
          const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
          if (onCustomConfigsChange) {
            onCustomConfigsChange(selectedConfigs);
          }
        }
      }
    } else {
      const current = providerHitl[id] !== false;
      const updated = { ...providerHitl, [id]: !current };
      saveProviderHitl(updated);
    }
  };

  const handleFolioThresholdChange = (id: string, value: number, isCustomConfig: boolean) => {
    if (disabled) return;
    const clamped = Math.max(0, Math.min(10, value));

    if (isCustomConfig) {
      const configIndex = customConfigs.findIndex(c => c.id === id);
      if (configIndex !== -1) {
        const updatedConfigs = [...customConfigs];
        updatedConfigs[configIndex] = {
          ...updatedConfigs[configIndex],
          folioThreshold: clamped
        };
        saveCustomConfigs(updatedConfigs);

        if (selectedCustomConfigIds.includes(id)) {
          const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
          if (onCustomConfigsChange) {
            onCustomConfigsChange(selectedConfigs);
          }
        }
      }
    } else {
      const updated = { ...providerFolioThreshold, [id]: clamped };
      saveProviderFolio(updated);
    }
  };

  const handleRelevanceCheckToggle = (id: string, isCustomConfig: boolean) => {
    if (disabled) return;

    if (isCustomConfig) {
      const configIndex = customConfigs.findIndex(c => c.id === id);
      if (configIndex !== -1) {
        const updatedConfigs = [...customConfigs];
        updatedConfigs[configIndex] = {
          ...updatedConfigs[configIndex],
          enableRelevanceCheck: !updatedConfigs[configIndex].enableRelevanceCheck
        };
        saveCustomConfigs(updatedConfigs);

        if (selectedCustomConfigIds.includes(id)) {
          const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
          if (onCustomConfigsChange) {
            onCustomConfigsChange(selectedConfigs);
          }
        }
      }
    } else {
      const current = providerRelevanceCheck[id] || false;
      const updated = { ...providerRelevanceCheck, [id]: !current };
      saveProviderRelevanceCheck(updated);
    }
  };

  const handleMinSearchScoreChange = (id: string, value: number, isCustomConfig: boolean) => {
    if (disabled) return;
    if (value <= 0) return; // Must be > 0

    if (isCustomConfig) {
      const configIndex = customConfigs.findIndex(c => c.id === id);
      if (configIndex !== -1) {
        const updatedConfigs = [...customConfigs];
        updatedConfigs[configIndex] = {
          ...updatedConfigs[configIndex],
          minimumSearchScore: value
        };
        saveCustomConfigs(updatedConfigs);

        if (selectedCustomConfigIds.includes(id)) {
          const selectedConfigs = updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id));
          if (onCustomConfigsChange) {
            onCustomConfigsChange(selectedConfigs);
          }
        }
      }
    } else {
      const updated = { ...providerMinSearchScore, [id]: value };
      saveProviderMinSearchScore(updated);
    }
  };

  // Expose handleSelectAll to parent via ref
  useEffect(() => {
    if (selectAllRef) {
      selectAllRef.current = handleSelectAll;
    }
    return () => {
      if (selectAllRef) {
        selectAllRef.current = null;
      }
    };
  }, [providers, selectedProviders, disabled, selectAllRef]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading providers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
        Error loading providers: {error}
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-700">
        No providers available
      </div>
    );
  }

  const allModelsWithProvider: ModelWithProvider[] = Object.entries(availableModels).flatMap(
    ([providerType, models]) => models.map(m => ({ ...m, providerType }))
  );
  const models = tempConfig.providerType ? (availableModels[tempConfig.providerType] || []) : [];
  const modelInfo = models.find(m => m.modelId === tempConfig.modelId);

  return (
    <div data-component="ProviderSelector">
      {/* Action Buttons */}
      {!hideAdvancedToggle && !isCreatingNew && (
        <div className="mb-4 flex items-center justify-between">
          {showCost && <AgentFlowDiagram highlightedAgent={hoveredAgent} onAgentHover={setHoveredAgent} />}
          <div className={`flex gap-2 flex-shrink-0${showCost ? ' ml-4' : ' ml-auto'}`}>
            <button
              onClick={handleImportConfig}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Import Config
            </button>
            <button
              onClick={handleStartNewConfig}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              New Config
            </button>
          </div>
        </div>
      )}
      
      {/* Show New Config and Import buttons for evaluation modes even when hideAdvancedToggle is true */}
      {hideAdvancedToggle && showOnlyCustomConfigs && !isCreatingNew && (
        <div className="mb-4 flex items-center justify-between">
          {showCost && <AgentFlowDiagram highlightedAgent={hoveredAgent} onAgentHover={setHoveredAgent} />}
          <div className={`flex gap-2 flex-shrink-0${showCost ? ' ml-4' : ' ml-auto'}`}>
            <button
              onClick={handleImportConfig}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Import Config
            </button>
            <button
              onClick={handleStartNewConfig}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              New Config
            </button>
          </div>
        </div>
      )}

      {/* New Config Creation */}
      {isCreatingNew && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newConfigName}
              onChange={(e) => setNewConfigName(e.target.value)}
              placeholder="Configuration name (e.g., Fast Research)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              onClick={handleSaveNewConfig}
              className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              Create
            </button>
            <button
              onClick={handleCancelNewConfig}
              className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {!hideSelectColumn && <th className="px-4 py-2 text-left font-semibold text-gray-700 w-12"></th>}
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Provider</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Embedding Index</th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700"
                onMouseEnter={() => setHoveredAgent('orchestrator')}
                onMouseLeave={() => setHoveredAgent(null)}
              >
                Orchestrator
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title={AGENT_DESCRIPTIONS.orchestrator}
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700"
                onMouseEnter={() => setHoveredAgent('researcher')}
                onMouseLeave={() => setHoveredAgent(null)}
              >
                Researcher
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title={AGENT_DESCRIPTIONS.researcher}
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700"
                onMouseEnter={() => setHoveredAgent('relevancechecker')}
                onMouseLeave={() => setHoveredAgent(null)}
              >
                Relevance
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title={AGENT_DESCRIPTIONS.relevancechecker}
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700"
                onMouseEnter={() => setHoveredAgent('reducer')}
                onMouseLeave={() => setHoveredAgent(null)}
              >
                Reducer
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title={AGENT_DESCRIPTIONS.reducer}
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-left font-semibold text-gray-700"
                onMouseEnter={() => setHoveredAgent('writer')}
                onMouseLeave={() => setHoveredAgent(null)}
              >
                Writer
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title={AGENT_DESCRIPTIONS.writer}
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Use Reducer</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">
                HITL
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="Human-in-the-Loop: When enabled, the AI will ask clarifying questions instead of guessing when your query is unclear. Your answers are used to improve the research results."
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">
                FOLIO
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="FOLIO runs when orchestrator complexity score > this value. 0 = always, 10 = disabled."
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">
                Relevance
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="Relevance Check: When enabled, the RelevanceChecker agent scores and filters search results before passing to the Writer. Disabled by default."
                >
                  i
                </span>
              </th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Max Supporting</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Max Contrary</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Max Passages</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">
                Min Score
                <span
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs text-gray-500 bg-gray-200 rounded-full cursor-help"
                  title="Minimum Elasticsearch score threshold. Results below this are discarded. Default: 0.7. Recommended: 0.0–1.0."
                >
                  i
                </span>
              </th>
              {!hideActions && <th className="px-4 py-2 w-12"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!showOnlyCustomConfigs && providers.map((provider) => {
              const providerWarnings = getRowWarnings({
                useReducer: providerReducer[provider.id] !== false,
                enableRelevanceCheck: providerRelevanceCheck[provider.id] || false,
                maxOpinions: providerMaxOpinions[provider.id] || 5,
                maxContraryOpinions: providerMaxContraryOpinions[provider.id] || Math.ceil((providerMaxOpinions[provider.id] || 5) / 2),
                maxPassages: providerMaxPassages[provider.id] || 3,
                agentConfigs: { orchestrator: undefined, researcher: undefined, reducer: undefined, writer: undefined, summarizer: undefined, relevancechecker: undefined }
              });
              return (
              <tr
                key={provider.id}
                className={`transition-colors ${
                  selectedProviders.includes(provider.id)
                    ? 'bg-blue-50'
                    : 'bg-white hover:bg-gray-50'
                } ${
                  !provider.isEnabled ? 'opacity-50' : ''
                }`}
              >
                {!hideSelectColumn && (
                  <td className="px-4 py-2">
                    <input
                      type={mode === 'evaluation-single' ? 'radio' : 'checkbox'}
                      checked={selectedProviders.includes(provider.id)}
                      onChange={() => handleProviderToggle(provider.id)}
                      disabled={!provider.isEnabled || disabled}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                    />
                  </td>
                )}
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{provider.displayName}</span>
                    {provider.isDefault && (
                      <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                        Default
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span
                    className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                    onClick={() => handleOpenEmbeddingIndexDialog(provider.id, false)}
                    title={embeddingIndexes.find(idx => idx.indexName === providerEmbeddingIndexes[provider.id])?.description || 'Click to select embedding index'}
                  >
                    {providerEmbeddingIndexes[provider.id] || embeddingIndexes.find(idx => idx.isDefault)?.indexName || 'Default'}
                  </span>
                </td>
                {(['orchestrator', 'researcher', 'relevancechecker', 'reducer', 'writer'] as const).map(agent => {
                  const modelId = provider.agentModels[agent];
                  const model = modelId ? (availableModels[provider.providerType] || []).find(m => m.modelId === modelId) : undefined;
                  return (
                    <td key={agent} className="px-4 py-2 text-gray-600">
                      <div>{modelId || '-'}</div>
                      {showCost && model && (model.inputTokenCostPer1M != null || model.outputTokenCostPer1M != null) && (
                        <div className="text-xs text-gray-500">
                          ({[
                            model.inputTokenCostPer1M != null ? `input: $${model.inputTokenCostPer1M}` : null,
                            model.outputTokenCostPer1M != null ? `output: $${model.outputTokenCostPer1M}` : null
                          ].filter(Boolean).join(', ')})
                        </div>
                      )}
                      {showCost && model?.contextWindowSize != null && (
                        <div className="text-xs text-indigo-600" title={`Context window: ${model.contextWindowSize.toLocaleString()} tokens`}>
                          ctx: {model.contextWindowSize >= 1_000_000 ? `${(model.contextWindowSize / 1_000_000).toFixed(1)}M` : `${Math.round(model.contextWindowSize / 1000)}K`}
                        </div>
                      )}
                      {model && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs">
                          {model.isThinkingModel && model.defaultReasoningEffort && (
                            <span className="flex items-center gap-0.5 text-purple-600" title="Reasoning effort">
                              <Brain className="w-3 h-3" />
                              {model.defaultReasoningEffort.charAt(0).toUpperCase() + model.defaultReasoningEffort.slice(1)}
                            </span>
                          )}
                          {!(model.isThinkingModel && model.defaultReasoningEffort && model.defaultReasoningEffort !== 'disabled' && model.defaultReasoningEffort !== 'none') && (
                            <>
                              {model.isTemperatureAllowed && model.defaultTemperature != null && (
                                <span className="flex items-center gap-0.5 text-orange-600" title={`Temperature: ${model.defaultTemperature}`}>
                                  <Thermometer className="w-3 h-3" />
                                  {model.defaultTemperature.toFixed(1)}
                                </span>
                              )}
                              {model.defaultTopP != null && (
                                <span className="flex items-center gap-0.5 text-blue-600" title={`Top P: ${model.defaultTopP}`}>
                                  <Target className="w-3 h-3" />
                                  {model.defaultTopP.toFixed(2)}
                                </span>
                              )}
                              {model.defaultSeed != null && (
                                <span className="flex items-center gap-0.5 text-green-600" title={`Seed: ${model.defaultSeed}`}>
                                  <Dices className="w-3 h-3" />
                                  {model.defaultSeed}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={providerReducer[provider.id] !== false}
                    onChange={() => {
                      const current = providerReducer[provider.id] !== false;
                      const updated = { ...providerReducer, [provider.id]: !current };
                      saveProviderReducer(updated);
                    }}
                    disabled={disabled}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                    title={providerReducer[provider.id] !== false ? "Reducer enabled for this provider" : "Reducer disabled for this provider"}
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={providerHitl[provider.id] !== false}
                    onChange={() => handleHitlToggle(provider.id, false)}
                    disabled={disabled}
                    className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer disabled:cursor-not-allowed"
                    title={providerHitl[provider.id] !== false ? "HITL enabled for this provider" : "HITL disabled for this provider"}
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={providerFolioThreshold[provider.id] ?? 6}
                    onChange={(e) => handleFolioThresholdChange(provider.id, parseInt(e.target.value) || 0, false)}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
                    title="FOLIO runs when orchestrator complexity score > this value. 0 = always, 10 = disabled."
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={providerRelevanceCheck[provider.id] || false}
                    onChange={() => handleRelevanceCheckToggle(provider.id, false)}
                    disabled={disabled}
                    className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer disabled:cursor-not-allowed"
                    title={providerRelevanceCheck[provider.id] ? "Relevance check enabled for this provider" : "Relevance check disabled for this provider"}
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={providerMaxOpinions[provider.id] || 5}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value) || 1;
                      const value = Math.max(1, Math.min(50, raw));
                      const updated = { ...providerMaxOpinions, [provider.id]: value };
                      saveProviderMaxOpinions(updated);
                    }}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    title="Max supporting opinions. Default: 5. Range: 1–50."
                  />
                  {providerWarnings.opinions && <WarningIcon message="Without Reducer, high total opinions may exceed context window and degrade Writer output" />}
                  {providerWarnings.relevance && <WarningIcon message="Relevance check processes all opinions at once — high total may blow up token budget" />}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center">
                  <input
                    type="number"
                    min="1"
                    max="25"
                    value={providerMaxContraryOpinions[provider.id] || Math.ceil((providerMaxOpinions[provider.id] || 5) / 2)}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value) || 1;
                      const value = Math.max(1, Math.min(25, raw));
                      const updated = { ...providerMaxContraryOpinions, [provider.id]: value };
                      saveProviderMaxContraryOpinions(updated);
                    }}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    title="Max contrary opinions. Default: ceil(supporting/2). Range: 1–25."
                  />
                  {providerWarnings.opinions && <WarningIcon message="Without Reducer, high total opinions may exceed context window and degrade Writer output" />}
                  {providerWarnings.relevance && <WarningIcon message="Relevance check processes all opinions at once — high total may blow up token budget" />}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={providerMaxPassages[provider.id] || 3}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value) || 1;
                      const value = Math.max(1, Math.min(20, raw));
                      const updated = { ...providerMaxPassages, [provider.id]: value };
                      saveProviderMaxPassages(updated);
                    }}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    title="Max passages per document. Default: 3. Range: 1–20."
                  />
                  {providerWarnings.passages && <WarningIcon message="More than 10 passages per document significantly increases token usage and may degrade output quality" />}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="number"
                    min="0.01"
                    max="10"
                    step="0.01"
                    value={providerMinSearchScore[provider.id] ?? 0.7}
                    onChange={(e) => handleMinSearchScoreChange(provider.id, parseFloat(e.target.value) || 0.7, false)}
                    disabled={disabled}
                    className="w-16 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    title="Minimum ES score threshold. Default: 0.7. Recommended: 0.0–1.0."
                  />
                </td>
                {!hideActions && (
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleCopyProviderAsConfig(provider)}
                      className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded relative"
                      title="Copy as custom configuration"
                    >
                      <Copy className="w-4 h-4" />
                      {copyFeedback === provider.id && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          Copied!
                        </span>
                      )}
                    </button>
                  </td>
                )}
              </tr>
            );
            })}
            
            {/* Custom Config Rows - Always shown when there are custom configs or showOnlyCustomConfigs is true */}
            {(customConfigs.length > 0 || showOnlyCustomConfigs) && customConfigs.map(config => {
              // In evaluation modes, use selectedProviders; otherwise use selectedCustomConfigIds
              const isSelected = (mode === 'evaluation-single' || mode === 'evaluation-multi') 
                ? selectedProviders.includes(config.id)
                : selectedCustomConfigIds.includes(config.id);

              const configWarnings = getRowWarnings({
                useReducer: config.config.reducer?.useReducer || false,
                enableRelevanceCheck: config.enableRelevanceCheck || false,
                maxOpinions: config.maxOpinions || 5,
                maxContraryOpinions: config.maxContraryOpinions || Math.ceil((config.maxOpinions || 5) / 2),
                maxPassages: config.maxPassagesPerDocument || 3,
                agentConfigs: config.config
              });
              
              return (
                <tr key={config.id} className={isSelected ? 'bg-purple-100' : 'bg-purple-50'}>
                  {!hideSelectColumn && (
                    <td className="px-4 py-2">
                      <input
                        type={mode === 'evaluation-single' ? 'radio' : 'checkbox'}
                        checked={isSelected}
                        onChange={() => handleCustomConfigSelect(config.id)}
                        disabled={disabled}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed"
                      />
                    </td>
                  )}
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-purple-600" />
                    {editingNameConfigId === config.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editingNameValue}
                          onChange={(e) => setEditingNameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEditName();
                            if (e.key === 'Escape') handleCancelEditName();
                          }}
                          className="px-2 py-1 text-sm border border-purple-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                          autoFocus
                        />
                        <button
                          onClick={handleSaveEditName}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEditName}
                          className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span
                        className="font-medium text-purple-900 cursor-pointer hover:text-purple-700"
                        onClick={() => handleStartEditName(config.id, config.name)}
                        title="Click to edit name"
                      >
                        {config.name}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span
                    className="cursor-pointer text-purple-600 hover:text-purple-800 hover:underline"
                    onClick={() => handleOpenEmbeddingIndexDialog(config.id, true)}
                    title={embeddingIndexes.find(idx => idx.indexName === config.embeddingIndex)?.description || 'Click to select embedding index'}
                  >
                    {config.embeddingIndex || embeddingIndexes.find(idx => idx.isDefault)?.indexName || 'Default'}
                  </span>
                </td>
                {(['orchestrator', 'researcher', 'relevancechecker', 'reducer', 'writer'] as AgentType[]).map(agentType => {
                  const agentConfig = config.config[agentType];
                  const cellModel = agentConfig?.providerType && agentConfig?.modelId
                    ? (availableModels[agentConfig.providerType] || []).find(m => m.modelId === agentConfig.modelId)
                    : undefined;
                  return (
                    <td
                      key={agentType}
                      className="px-4 py-2 cursor-pointer hover:bg-purple-200 transition-colors"
                      onClick={() => handleCellClick(config.id, agentType)}
                    >
                      {agentConfig?.providerType && agentConfig?.modelId ? (
                        <div className="text-xs">
                          <div className="font-medium text-purple-900">{agentConfig.providerType}</div>
                          <div className="text-purple-700">{agentConfig.modelId}</div>
                          {showCost && cellModel && (cellModel.inputTokenCostPer1M != null || cellModel.outputTokenCostPer1M != null) && (
                            <div className="text-purple-500">
                              ({[
                                cellModel.inputTokenCostPer1M != null ? `input: $${cellModel.inputTokenCostPer1M}` : null,
                                cellModel.outputTokenCostPer1M != null ? `output: $${cellModel.outputTokenCostPer1M}` : null
                              ].filter(Boolean).join(', ')})
                            </div>
                          )}
                          {showCost && cellModel?.contextWindowSize != null && (
                            <div className="text-indigo-600" title={`Context window: ${cellModel.contextWindowSize.toLocaleString()} tokens`}>
                              ctx: {cellModel.contextWindowSize >= 1_000_000 ? `${(cellModel.contextWindowSize / 1_000_000).toFixed(1)}M` : `${Math.round(cellModel.contextWindowSize / 1000)}K`}
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            {agentConfig.reasoningEffort && (
                              <span className="flex items-center gap-0.5 text-purple-600" title="Reasoning effort">
                                <Brain className="w-3 h-3" />
                                {agentConfig.reasoningEffort.charAt(0).toUpperCase() + agentConfig.reasoningEffort.slice(1)}
                              </span>
                            )}
                            {!(agentConfig.reasoningEffort && agentConfig.reasoningEffort !== 'disabled' && agentConfig.reasoningEffort !== 'none') && (
                              <>
                                {agentConfig.temperature !== undefined && agentConfig.temperature !== null && (
                                  <span className="flex items-center gap-0.5 text-orange-600" title={`Temperature: ${agentConfig.temperature}`}>
                                    <Thermometer className="w-3 h-3" />
                                    {agentConfig.temperature.toFixed(2)}
                                  </span>
                                )}
                                {agentConfig.topP !== undefined && agentConfig.topP !== null && (
                                  <span className="flex items-center gap-0.5 text-blue-600" title={`Top P: ${agentConfig.topP}`}>
                                    <Target className="w-3 h-3" />
                                    {agentConfig.topP.toFixed(2)}
                                  </span>
                                )}
                                {agentConfig.seed !== undefined && agentConfig.seed !== null && (
                                  <span className="flex items-center gap-0.5 text-green-600" title={`Seed: ${agentConfig.seed}`}>
                                    <Dices className="w-3 h-3" />
                                    {agentConfig.seed}
                                  </span>
                                )}
                                {agentConfig.frequencyPenalty !== undefined && agentConfig.frequencyPenalty !== null && agentConfig.frequencyPenalty !== 0 && (
                                  <span className="text-gray-600" title={`Frequency Penalty: ${agentConfig.frequencyPenalty}`}>
                                    FP:{agentConfig.frequencyPenalty.toFixed(2)}
                                  </span>
                                )}
                                {agentConfig.presencePenalty !== undefined && agentConfig.presencePenalty !== null && agentConfig.presencePenalty !== 0 && (
                                  <span className="text-gray-600" title={`Presence Penalty: ${agentConfig.presencePenalty}`}>
                                    PP:{agentConfig.presencePenalty.toFixed(2)}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {configWarnings.prompts.includes(agentType) && (
                            <div className="mt-0.5">
                              <WarningIcon message={`System prompt is not the newest version for ${AGENT_LABELS[agentType]} — using an older or non-default prompt`} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">Click to configure</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={config.config.reducer?.useReducer || false}
                    onChange={() => handleReducerToggle(config.id, config.config.reducer?.useReducer || false)}
                    disabled={disabled}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed"
                    title="Enable/disable Reducer agent for this configuration"
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={config.enableHitl !== false}
                    onChange={() => handleHitlToggle(config.id, true)}
                    disabled={disabled}
                    className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer disabled:cursor-not-allowed"
                    title={config.enableHitl !== false ? "HITL enabled for this configuration" : "HITL disabled for this configuration"}
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={config.folioThreshold ?? 6}
                    onChange={(e) => handleFolioThresholdChange(config.id, parseInt(e.target.value) || 0, true)}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
                    title="FOLIO runs when orchestrator complexity score > this value. 0 = always, 10 = disabled."
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={config.enableRelevanceCheck || false}
                    onChange={() => handleRelevanceCheckToggle(config.id, true)}
                    disabled={disabled}
                    className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer disabled:cursor-not-allowed"
                    title={config.enableRelevanceCheck ? "Relevance check enabled for this configuration" : "Relevance check disabled for this configuration"}
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={config.maxOpinions || 5}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value) || 1;
                      const value = Math.max(1, Math.min(50, raw));
                      const configIndex = customConfigs.findIndex(c => c.id === config.id);
                      if (configIndex !== -1) {
                        const updatedConfigs = [...customConfigs];
                        updatedConfigs[configIndex] = { ...updatedConfigs[configIndex], maxOpinions: value };
                        saveCustomConfigs(updatedConfigs);
                        if (selectedCustomConfigIds.includes(config.id) && onCustomConfigsChange) {
                          onCustomConfigsChange(updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id)));
                        }
                      }
                    }}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    title="Max supporting opinions. Default: 5. Range: 1–50."
                  />
                  {configWarnings.opinions && <WarningIcon message="Without Reducer, high total opinions may exceed context window and degrade Writer output" />}
                  {configWarnings.relevance && <WarningIcon message="Relevance check processes all opinions at once — high total may blow up token budget" />}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center">
                  <input
                    type="number"
                    min="1"
                    max="25"
                    value={config.maxContraryOpinions || Math.ceil((config.maxOpinions || 5) / 2)}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value) || 1;
                      const value = Math.max(1, Math.min(25, raw));
                      const configIndex = customConfigs.findIndex(c => c.id === config.id);
                      if (configIndex !== -1) {
                        const updatedConfigs = [...customConfigs];
                        updatedConfigs[configIndex] = { ...updatedConfigs[configIndex], maxContraryOpinions: value };
                        saveCustomConfigs(updatedConfigs);
                        if (selectedCustomConfigIds.includes(config.id) && onCustomConfigsChange) {
                          onCustomConfigsChange(updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id)));
                        }
                      }
                    }}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    title="Max contrary opinions. Default: ceil(supporting/2). Range: 1–25."
                  />
                  {configWarnings.opinions && <WarningIcon message="Without Reducer, high total opinions may exceed context window and degrade Writer output" />}
                  {configWarnings.relevance && <WarningIcon message="Relevance check processes all opinions at once — high total may blow up token budget" />}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={config.maxPassagesPerDocument || 3}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value) || 1;
                      const value = Math.max(1, Math.min(20, raw));
                      const configIndex = customConfigs.findIndex(c => c.id === config.id);
                      if (configIndex !== -1) {
                        const updatedConfigs = [...customConfigs];
                        updatedConfigs[configIndex] = { ...updatedConfigs[configIndex], maxPassagesPerDocument: value };
                        saveCustomConfigs(updatedConfigs);
                        if (selectedCustomConfigIds.includes(config.id) && onCustomConfigsChange) {
                          onCustomConfigsChange(updatedConfigs.filter(c => selectedCustomConfigIds.includes(c.id)));
                        }
                      }
                    }}
                    disabled={disabled}
                    className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    title="Max passages per document. Default: 3. Range: 1–20."
                  />
                  {configWarnings.passages && <WarningIcon message="More than 10 passages per document significantly increases token usage and may degrade output quality" />}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <input
                    type="number"
                    min="0.01"
                    max="10"
                    step="0.01"
                    value={config.minimumSearchScore ?? 0.7}
                    onChange={(e) => handleMinSearchScoreChange(config.id, parseFloat(e.target.value) || 0.7, true)}
                    disabled={disabled}
                    className="w-16 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    title="Minimum ES score threshold. Default: 0.7. Recommended: 0.0–1.0."
                  />
                </td>
                {!hideActions && (
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleCopyConfig(config.id)}
                        className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded relative"
                        title="Copy configuration"
                      >
                        <Copy className="w-4 h-4" />
                        {copyFeedback === config.id && (
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                            Copied!
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteConfig(config.id)}
                        className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Delete configuration"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
            })}
            
            {/* Saved Evaluation Config Rows - Read-only display of configs from evaluation test */}
            {savedEvaluationConfigs && savedEvaluationConfigs.length > 0 && savedEvaluationConfigs.map((savedConfig) => {
              // All evaluation configs are custom configs (stored with full agent model details)
              // Check if a matching custom config exists in localStorage
              const matchingConfig = customConfigs.find(c => {
                const orchestratorMatch = 
                  c.config.orchestrator?.providerType === savedConfig.orchestrator?.providerType &&
                  c.config.orchestrator?.modelId === savedConfig.orchestrator?.modelId;
                const researcherMatch = 
                  c.config.researcher?.providerType === savedConfig.researcher?.providerType &&
                  c.config.researcher?.modelId === savedConfig.researcher?.modelId;
                const writerMatch = 
                  c.config.writer?.providerType === savedConfig.writer?.providerType &&
                  c.config.writer?.modelId === savedConfig.writer?.modelId;
                
                return orchestratorMatch && researcherMatch && writerMatch;
              });
              const isMissing = !matchingConfig;
              
              return (
                <tr key={`saved-${savedConfig.id}`} className={isMissing ? 'bg-orange-100' : 'bg-orange-50'} title="Saved configuration from evaluation test (read-only)">
                  {!hideSelectColumn && (
                    <td className="px-4 py-2">
                      <span className="text-xs text-orange-700 font-medium">SAVED</span>
                    </td>
                  )}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-orange-900">
                        {savedConfig.name}
                      </span>
                      {isMissing && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                          Not in Local Configs
                        </span>
                      )}
                      <span className="text-xs px-1.5 py-0.5 bg-orange-200 text-orange-800 rounded">
                        Custom Config
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-orange-700 text-xs">
                    {savedConfig.embeddingIndex || 'Default'}
                  </td>
                  {(['orchestrator', 'researcher', 'relevancechecker', 'reducer', 'writer'] as const).map(agentType => {
                    const agentConfig = savedConfig[agentType];
                    const savedCellModel = agentConfig?.providerType && agentConfig?.modelId
                      ? (availableModels[agentConfig.providerType] || []).find(m => m.modelId === agentConfig.modelId)
                      : undefined;
                    return (
                      <td key={agentType} className="px-4 py-2">
                        {agentConfig?.providerType && agentConfig?.modelId ? (
                          <div className="text-xs">
                            <div className="font-medium text-orange-900">{agentConfig.providerType}</div>
                            <div className="text-orange-700">{agentConfig.modelId}</div>
                            {showCost && savedCellModel && (savedCellModel.inputTokenCostPer1M != null || savedCellModel.outputTokenCostPer1M != null) && (
                              <div className="text-orange-500">
                                ({[
                                  savedCellModel.inputTokenCostPer1M != null ? `input: $${savedCellModel.inputTokenCostPer1M}` : null,
                                  savedCellModel.outputTokenCostPer1M != null ? `output: $${savedCellModel.outputTokenCostPer1M}` : null
                                ].filter(Boolean).join(', ')})
                              </div>
                            )}
                            {showCost && savedCellModel?.contextWindowSize != null && (
                              <div className="text-indigo-600" title={`Context window: ${savedCellModel.contextWindowSize.toLocaleString()} tokens`}>
                                ctx: {savedCellModel.contextWindowSize >= 1_000_000 ? `${(savedCellModel.contextWindowSize / 1_000_000).toFixed(1)}M` : `${Math.round(savedCellModel.contextWindowSize / 1000)}K`}
                              </div>
                            )}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                              {agentConfig.reasoningEffort && (
                                <span className="flex items-center gap-0.5 text-purple-600" title="Reasoning effort">
                                  <Brain className="w-3 h-3" />
                                  {agentConfig.reasoningEffort.charAt(0).toUpperCase() + agentConfig.reasoningEffort.slice(1)}
                                </span>
                              )}
                              {!(agentConfig.reasoningEffort && agentConfig.reasoningEffort !== 'disabled' && agentConfig.reasoningEffort !== 'none') && (
                                <>
                                  {agentConfig.temperature !== undefined && agentConfig.temperature !== null && (
                                    <span className="flex items-center gap-0.5 text-orange-600" title={`Temperature: ${agentConfig.temperature}`}>
                                      <Thermometer className="w-3 h-3" />
                                      {agentConfig.temperature.toFixed(2)}
                                    </span>
                                  )}
                                  {agentConfig.topP !== undefined && agentConfig.topP !== null && (
                                    <span className="flex items-center gap-0.5 text-blue-600" title={`Top P: ${agentConfig.topP}`}>
                                      <Target className="w-3 h-3" />
                                      {agentConfig.topP.toFixed(2)}
                                    </span>
                                  )}
                                  {agentConfig.seed !== undefined && agentConfig.seed !== null && (
                                    <span className="flex items-center gap-0.5 text-green-600" title={`Seed: ${agentConfig.seed}`}>
                                      <Dices className="w-3 h-3" />
                                      {agentConfig.seed}
                                    </span>
                                  )}
                                  {agentConfig.frequencyPenalty !== undefined && agentConfig.frequencyPenalty !== null && agentConfig.frequencyPenalty !== 0 && (
                                    <span className="text-gray-600" title={`Frequency Penalty: ${agentConfig.frequencyPenalty}`}>
                                      FP:{agentConfig.frequencyPenalty.toFixed(2)}
                                    </span>
                                  )}
                                  {agentConfig.presencePenalty !== undefined && agentConfig.presencePenalty !== null && agentConfig.presencePenalty !== 0 && (
                                    <span className="text-gray-600" title={`Presence Penalty: ${agentConfig.presencePenalty}`}>
                                      PP:{agentConfig.presencePenalty.toFixed(2)}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-orange-400 text-xs">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={savedConfig.useReducer || false}
                      disabled
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded cursor-not-allowed opacity-50"
                      title={savedConfig.useReducer ? "Reducer was enabled" : "Reducer was disabled"}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded cursor-not-allowed opacity-50"
                      title="HITL setting (read-only)"
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="number"
                      value={0}
                      disabled
                      className="w-14 px-1 py-0.5 text-sm text-center border border-gray-300 rounded bg-gray-100 opacity-50 cursor-not-allowed"
                      title="FOLIO setting (read-only)"
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded cursor-not-allowed opacity-50"
                      title="Relevance check setting (read-only)"
                    />
                  </td>
                  <td className="px-4 py-2 text-center text-orange-700 text-xs">
                    {savedConfig.maxOpinions || 5}
                  </td>
                  <td className="px-4 py-2 text-center text-orange-700 text-xs">
                    {(savedConfig as any).maxContraryOpinions || Math.ceil((savedConfig.maxOpinions || 5) / 2)}
                  </td>
                  <td className="px-4 py-2 text-center text-orange-700 text-xs">
                    {savedConfig.maxPassagesPerDocument || 3}
                  </td>
                  <td className="px-4 py-2 text-center text-orange-700 text-xs">
                    {(savedConfig as any).minimumSearchScore ?? 0.7}
                  </td>
                  {!hideActions && (
                    <td className="px-4 py-2">
                      <span className="text-xs text-orange-600">Read-only</span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Model Configuration Popup */}
      {editingAgent && editingConfigId && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCancelEdit} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col" style={{ width: '80vw', minHeight: '45vh' }}>
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
                <h3 className="text-lg font-semibold text-gray-900">
                  Configure {AGENT_LABELS[editingAgent]}
                </h3>
                <button onClick={handleCancelEdit} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="px-6 py-4 space-y-4 overflow-auto flex-1">
                {/* Selected Provider (read-only indicator) */}
                {tempConfig.providerType && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium">Selected Provider:</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">{tempConfig.providerType}</span>
                  </div>
                )}

                {/* Model Selection (all providers) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <ModelTableDropdown
                    models={allModelsWithProvider}
                    selectedModelId={tempConfig.modelId}
                    onSelect={handleModelChange}
                    showCost={showCost}
                  />
                </div>

                {/* System Prompt Selection */}
                <div data-flow="system-prompt-selector">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    System Prompt (Optional)
                  </label>
                  <div className="flex gap-2">
                    <SystemPromptTableDropdown
                      prompts={systemPrompts.filter(p => editingAgent && p.agentType === editingAgent)}
                      selectedHash={tempConfig.systemPromptHash || ''}
                      onSelect={(hash) => handleSystemPromptChange(hash)}
                    />
                    {systemPrompts.filter(p => editingAgent && p.agentType === editingAgent).length >= 2 && (
                      <button
                        onClick={handleOpenPromptComparison}
                        className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200"
                        title="Compare system prompt versions"
                      >
                        <GitCompare className="w-4 h-4" />
                        Compare
                      </button>
                    )}
                  </div>
                  {tempConfig.systemPromptHash && (
                    <p className="text-xs text-gray-600 mt-1">
                      {systemPrompts.find(p => p.hash === tempConfig.systemPromptHash)?.purpose || ''}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select a custom system prompt to override the default agent instructions
                  </p>
                </div>

                {/* Reasoning Effort Dropdown */}
                {modelInfo?.isThinkingModel && modelInfo?.allowedReasoningEfforts && modelInfo.allowedReasoningEfforts.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reasoning Effort
                    </label>
                    <select
                      value={tempConfig.reasoningEffort ?? ''}
                      onChange={(e) => handleReasoningEffortChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Use Default</option>
                      <option value="disabled">Disabled (use sampling params)</option>
                      {modelInfo.allowedReasoningEfforts.map(effort => (
                        <option key={effort} value={effort}>
                          {effort.charAt(0).toUpperCase() + effort.slice(1)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Controls how much reasoning effort the model uses. Select "Disabled" to use temperature/topP/etc. instead.
                    </p>
                  </div>
                )}

                {/* Reasoning active indicator — sampling params are incompatible */}
                {modelInfo?.isThinkingModel && tempConfig.reasoningEffort !== 'disabled' && tempConfig.reasoningEffort !== 'none' && (tempConfig.reasoningEffort || (modelInfo.defaultReasoningEffort && modelInfo.defaultReasoningEffort !== 'none')) && (
                  <div data-flow="reasoning-active-notice" className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                    <p className="text-sm text-amber-800 font-medium">
                      ⚡ Reasoning mode active — sampling parameters disabled
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Temperature, Top P, Seed, Frequency Penalty, and Presence Penalty are incompatible with reasoning and will be skipped by the backend.
                    </p>
                  </div>
                )}

                {/* Sampling parameters — hidden when reasoning is active */}
                {!(modelInfo?.isThinkingModel && tempConfig.reasoningEffort !== 'disabled' && tempConfig.reasoningEffort !== 'none' && (tempConfig.reasoningEffort || (modelInfo?.defaultReasoningEffort && modelInfo.defaultReasoningEffort !== 'none'))) && (
                  <>
                    {/* Temperature Slider */}
                    {modelInfo?.isTemperatureAllowed && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Temperature: {tempConfig.temperature?.toFixed(2) ?? modelInfo.defaultTemperature.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min={modelInfo.minTemperature}
                          max={modelInfo.maxTemperature}
                          step={0.01}
                          value={tempConfig.temperature ?? modelInfo.defaultTemperature}
                          onChange={(e) => handleTemperatureChange(parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{modelInfo.minTemperature}</span>
                          <span>Default: {modelInfo.defaultTemperature.toFixed(2)}</span>
                          <span>{modelInfo.maxTemperature}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Controls randomness. Lower = more deterministic, Higher = more creative
                        </p>
                      </div>
                    )}

                    {/* TopP Slider */}
                    {modelInfo?.defaultTopP !== undefined && modelInfo?.defaultTopP !== null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Top P (Nucleus Sampling): {tempConfig.topP?.toFixed(2) ?? modelInfo.defaultTopP.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min={modelInfo.minTopP ?? 0}
                          max={modelInfo.maxTopP ?? 1}
                          step={0.01}
                          value={tempConfig.topP ?? modelInfo.defaultTopP}
                          onChange={(e) => handleTopPChange(parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{modelInfo.minTopP ?? 0}</span>
                          <span>Default: {modelInfo.defaultTopP.toFixed(2)}</span>
                          <span>{modelInfo.maxTopP ?? 1}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Limits token selection to top probability mass. Lower = more focused
                        </p>
                      </div>
                    )}

                    {/* Seed Input */}
                    {modelInfo?.defaultSeed !== undefined && modelInfo?.defaultSeed !== null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Seed (for deterministic outputs)
                        </label>
                        <input
                          type="number"
                          value={tempConfig.seed ?? modelInfo.defaultSeed}
                          onChange={(e) => handleSeedChange(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`Default: ${modelInfo.defaultSeed}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Fixed seed makes outputs more reproducible (default: {modelInfo.defaultSeed})
                        </p>
                      </div>
                    )}

                    {/* Frequency Penalty Slider */}
                    {modelInfo?.defaultFrequencyPenalty !== undefined && modelInfo?.defaultFrequencyPenalty !== null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Frequency Penalty: {tempConfig.frequencyPenalty?.toFixed(2) ?? modelInfo.defaultFrequencyPenalty.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min={modelInfo.minFrequencyPenalty ?? -2}
                          max={modelInfo.maxFrequencyPenalty ?? 2}
                          step={0.01}
                          value={tempConfig.frequencyPenalty ?? modelInfo.defaultFrequencyPenalty}
                          onChange={(e) => handleFrequencyPenaltyChange(parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{modelInfo.minFrequencyPenalty ?? -2}</span>
                          <span>Default: {modelInfo.defaultFrequencyPenalty.toFixed(2)}</span>
                          <span>{modelInfo.maxFrequencyPenalty ?? 2}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Reduces repetition based on token frequency. Positive = less repetition
                        </p>
                      </div>
                    )}

                    {/* Presence Penalty Slider */}
                    {modelInfo?.defaultPresencePenalty !== undefined && modelInfo?.defaultPresencePenalty !== null && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Presence Penalty: {tempConfig.presencePenalty?.toFixed(2) ?? modelInfo.defaultPresencePenalty.toFixed(2)}
                        </label>
                        <input
                          type="range"
                          min={modelInfo.minPresencePenalty ?? -2}
                          max={modelInfo.maxPresencePenalty ?? 2}
                          step={0.01}
                          value={tempConfig.presencePenalty ?? modelInfo.defaultPresencePenalty}
                          onChange={(e) => handlePresencePenaltyChange(parseFloat(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{modelInfo.minPresencePenalty ?? -2}</span>
                          <span>Default: {modelInfo.defaultPresencePenalty.toFixed(2)}</span>
                          <span>{modelInfo.maxPresencePenalty ?? 2}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Encourages new topics. Positive = more diverse content
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Info box about defaults */}
                {tempConfig.modelId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Parameters shown are those supported by this model. 
                      Defaults are optimized for consistent, deterministic results.
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 bg-white">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAgentConfig}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Import Configuration Dialog */}
      {showImportDialog && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCancelImport} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col" style={{ width: '80vw' }}>
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Import Configuration
                </h3>
                <button onClick={handleCancelImport} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="px-6 py-4 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Configuration JSON
                    </label>
                    <button
                      onClick={handlePasteConfig}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" />
                      Paste from clipboard
                    </button>
                  </div>
                  <textarea
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder='Paste configuration JSON here, e.g.:
{
  "version": 1,
  "name": "Fast Research",
  "config": {
    "orchestrator": {
      "providerType": "OpenAI",
      "modelId": "gpt-4",
      "temperature": 0.7
    },
    ...
  }
}'
                    className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> The configuration will be validated before import. 
                    If a configuration with the same name exists, it will be imported with a timestamp suffix.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={handleCancelImport}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleValidateAndImport}
                  disabled={!importText.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Validate & Import
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Embedding Index Selector Dialog */}
      <EmbeddingIndexSelector
        isOpen={showEmbeddingIndexDialog}
        embeddingIndexes={embeddingIndexes}
        currentSelection={embeddingIndexDialogTarget ? (
          embeddingIndexDialogTarget.isCustomConfig
            ? customConfigs.find(c => c.id === embeddingIndexDialogTarget.id)?.embeddingIndex
            : providerEmbeddingIndexes[embeddingIndexDialogTarget.id]
        ) : undefined}
        onSelect={handleSelectEmbeddingIndex}
        onClose={handleCancelEmbeddingIndexDialog}
      />

      {/* System Prompt Comparison Dialog */}
      <TextComparisonDialog
        isOpen={showPromptComparison}
        title="Compare System Prompts"
        items={promptComparisonItems}
        initialLeftId={tempConfig.systemPromptHash}
        fetchContent={fetchPromptContent}
        onApprove={handlePromptComparisonApprove}
        onClose={() => setShowPromptComparison(false)}
      />
    </div>
  );
};
